/**
 * /integration/connections/new — create a source connection.
 *
 * Source-type-aware form. The credential is entered once and sent to the
 * backend (which stores it in Key Vault); it is never read back. "Test"
 * runs an ad-hoc probe (FS-902) before saving so the operator gets a
 * verdict without persisting anything.
 */

import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Group,
  NumberInput,
  Paper,
  PasswordInput,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import {
  IconDeviceFloppy,
  IconInfoCircle,
  IconPlugConnected,
  IconSearch,
} from "@tabler/icons-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { LabelWithHelp } from "@/shared/ui/LabelWithHelp";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";
import { CONNECTION_HELP } from "./connectionHelp";
import {
  authModeNeedsCredential,
  createConnection,
  credentialLabel as credentialLabelFor,
  s3Credential,
  SOURCE_TYPES,
  testAdhocConnection,
  type CreateConnectionIn,
  type SourceType,
  type TestConnectionIn,
  type TestConnectionOut,
} from "@/shared/api/integration";
import { discoverDeployments, type DeploymentOut } from "@/shared/api/ai";
import { databaseEngines, type DatabaseEngine } from "@/shared/api/database";

const CLASSIFICATION_OPTIONS = [
  { value: "public", label: "Public" },
  { value: "confidential", label: "Confidential" },
  { value: "pii", label: "PII" },
  { value: "phi", label: "PHI" },
];

const AUTH_MODES: Record<SourceType, { value: string; label: string }[]> = {
  veeva: [
    { value: "password", label: "Service-account password" },
    { value: "oauth2", label: "OAuth2 access token" },
  ],
  blob: [{ value: "connection_string", label: "Connection string / SAS" }],
  // Strongest first — the default offer must not be the long-lived shared
  // secret (ADR-0021 §2).
  s3: [
    { value: "oidc_web_identity", label: "OIDC federation (no stored secret)" },
    { value: "assume_role", label: "Assume role + external ID" },
    { value: "access_key", label: "Access key" },
  ],
  // Endpoint + key is the whole setup. The backend also supports
  // `managed_identity` (no stored secret — the stronger posture, and what a
  // deployed environment should use), but it is not offered here: a managed
  // identity only exists inside Azure, so it cannot be set up or tested from
  // this form. Create one through the API when provisioning a deployed env.
  ai_foundry: [{ value: "api_key", label: "API key" }],
  azure_devops: [{ value: "pat", label: "Personal Access Token" }],
  // One mode, deliberately: a customer's database account is the only
  // credential a customer-hosted database has. An Entra-token mode for Azure
  // SQL / Azure Database for PostgreSQL is a separate piece of work
  // (ADR-0022 §2), not a half-built option here.
  database: [{ value: "password", label: "Database account" }],
  // CARA's REST API has one way in: an API token, exchanged for a session at
  // call time. There is no Entra / managed-identity path to offer.
  cara: [{ value: "api_token", label: "API token" }],
};

export function NewConnection() {
  const getToken = useAccessToken();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Pre-scope to the source type the operator picked on the Available tab.
  // A valid `?source=` locks the picker; anything else falls back to Veeva.
  const presetSource = searchParams.get("source");
  const sourceLocked = (SOURCE_TYPES as readonly string[]).includes(presetSource ?? "");
  const initialSource: SourceType = sourceLocked ? (presetSource as SourceType) : "veeva";

  const [sourceType, setSourceType] = useState<SourceType>(initialSource);
  const [displayName, setDisplayName] = useState("");
  const [classification, setClassification] = useState<string>("confidential");
  const [authMode, setAuthMode] = useState(AUTH_MODES[initialSource][0]?.value ?? "");
  const [credential, setCredential] = useState("");

  // Azure AI Foundry: an endpoint and a key, and that is the whole story.
  // Discovery is a data-plane call on the same endpoint with the same
  // credential, so nothing else is needed to populate the model picklist.
  const [aiEndpoint, setAiEndpoint] = useState("");
  const [discovered, setDiscovered] = useState<DeploymentOut[] | null>(null);
  const [chosenDeployments, setChosenDeployments] = useState<string[]>([]);

  // AWS S3 config fields. `region` is required for every S3 mode and is
  // recorded as residency evidence (ADR-0021 §3); the rest are mode-specific.
  const [region, setRegion] = useState("");
  const [roleArn, setRoleArn] = useState("");
  const [externalId, setExternalId] = useState("");
  const [accessKeyId, setAccessKeyId] = useState("");

  // Veeva config fields.
  const [vaultUrl, setVaultUrl] = useState("");
  const [username, setUsername] = useState("");
  // Default to the latest Veeva REST API version we target (kept in step with
  // the backend's DEFAULT_API_VERSION). Editable per connection if a vault
  // needs a specific version.
  const [apiVersion, setApiVersion] = useState("v26.1");
  // Azure DevOps config.
  const [adoOrgUrl, setAdoOrgUrl] = useState("https://dev.azure.com/");
  const [adoProject, setAdoProject] = useState("");
  // CARA config: the tenant base URL, which includes CARA's application path.
  const [caraBaseUrl, setCaraBaseUrl] = useState("");

  // Database config. The engine list comes from the backend so the form can
  // never offer an engine this build does not speak.
  const [engines, setEngines] = useState<DatabaseEngine[]>([]);
  const [dbEngine, setDbEngine] = useState("postgresql");
  const [dbHost, setDbHost] = useState("");
  const [dbPort, setDbPort] = useState<number | "">("");
  const [dbName, setDbName] = useState("");
  const [dbUsername, setDbUsername] = useState("");
  const [dbTls, setDbTls] = useState(true);
  const engineSpec = engines.find((e) => e.engine === dbEngine) ?? null;

  const [testResult, setTestResult] = useState<TestConnectionOut | null>(null);
  const [busy, setBusy] = useState<"test" | "save" | "discover" | null>(null);

  useEffect(() => {
    if (sourceType !== "database" || engines.length > 0) return;
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const result = await databaseEngines(token);
        if (!cancelled) setEngines(result.items);
      } catch (err) {
        toastApiError("Could not load the database engines", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sourceType, engines.length, getToken]);

  const onSourceChange = (next: string | null) => {
    const st = (next as SourceType) ?? "veeva";
    setSourceType(st);
    setAuthMode(AUTH_MODES[st][0]?.value ?? "");
    setTestResult(null);
    setDiscovered(null);
    setChosenDeployments([]);
  };

  // Shared with the detail page's rotate form, so the two never drift on what a
  // given source type's credential is called.
  const credentialLabel = credentialLabelFor(sourceType, authMode);

  const config = useMemo<Record<string, unknown>>(() => {
    if (sourceType === "veeva") {
      return {
        vault_url: vaultUrl.trim(),
        username: username.trim(),
        api_version: apiVersion.trim(),
      };
    }
    if (sourceType === "ai_foundry") {
      // Store the picked deployments WITH their model + pinned version. The
      // version is what makes an answer reproducible (CLAUDE.md §9), so it is
      // captured at save time rather than re-read later.
      const picked = (discovered ?? []).filter((d) => chosenDeployments.includes(d.name));
      return {
        endpoint: aiEndpoint.trim(),
        deployments: picked.map((d) => ({
          name: d.name,
          label: d.label,
          model: d.model,
          model_version: d.model_version,
        })),
        default_deployment: picked[0]?.name ?? "",
      };
    }
    if (sourceType === "azure_devops") {
      return { org_url: adoOrgUrl.trim(), project: adoProject.trim() };
    }
    if (sourceType === "database") {
      const addressesService = engineSpec?.addresses === "service_name";
      return {
        engine: dbEngine,
        host: dbHost.trim(),
        // Empty means "the engine's default", which the backend fills in —
        // repeating 5432 in every connection row teaches nothing.
        port: dbPort === "" ? null : dbPort,
        database: addressesService ? "" : dbName.trim(),
        service_name: addressesService ? dbName.trim() : "",
        username: dbUsername.trim(),
        tls: dbTls,
      };
    }
    if (sourceType === "cara") {
      return { base_url: caraBaseUrl.trim() };
    }
    if (sourceType === "s3") {
      const base: Record<string, unknown> = { region: region.trim() };
      if (authMode !== "access_key") base.role_arn = roleArn.trim();
      if (authMode === "assume_role") base.external_id = externalId.trim();
      return base;
    }
    return {};
  }, [
    sourceType,
    vaultUrl,
    username,
    apiVersion,
    aiEndpoint,
    discovered,
    chosenDeployments,
    adoOrgUrl,
    adoProject,
    dbEngine,
    dbHost,
    dbPort,
    dbName,
    dbUsername,
    dbTls,
    engineSpec,
    caraBaseUrl,
    authMode,
    region,
    roleArn,
    externalId,
  ]);

  /** S3 needs an access key *pair*, sent as the one JSON credential a
   *  connection stores (ADR-0007). Credential-less modes send nothing. */
  const isS3Credentialless = sourceType === "s3" && !authModeNeedsCredential(authMode);
  const effectiveCredential = useMemo<string>(() => {
    if (isS3Credentialless) return "";
    if (sourceType === "s3") return s3Credential(accessKeyId, credential);
    return credential;
  }, [sourceType, isS3Credentialless, accessKeyId, credential]);

  const s3Valid =
    region.trim().length > 0 &&
    (authMode === "access_key" || roleArn.trim().length > 0) &&
    (authMode !== "assume_role" || externalId.trim().length > 0) &&
    (isS3Credentialless || (accessKeyId.trim().length > 0 && credential.length > 0));

  // Azure AI Foundry has no credential to require; it needs an endpoint and at
  // least one selected deployment instead.
  const valid =
    displayName.trim().length > 0 &&
    (sourceType === "ai_foundry"
      ? aiEndpoint.trim().length > 0 && credential.length > 0 && chosenDeployments.length > 0
      : sourceType === "azure_devops"
        ? credential.length > 0 && adoOrgUrl.trim().length > 0 && adoProject.trim().length > 0
        : sourceType === "database"
          ? credential.length > 0 &&
            dbHost.trim().length > 0 &&
            dbName.trim().length > 0 &&
            dbUsername.trim().length > 0
          : sourceType === "cara"
            ? credential.length > 0 && caraBaseUrl.trim().length > 0
            : sourceType === "s3"
              ? s3Valid
              : credential.length > 0 &&
                (sourceType !== "veeva" || vaultUrl.trim().length > 0));

  const testPayload = (): TestConnectionIn => ({
    source_type: sourceType,
    auth_mode: authMode,
    config,
    // Never send an empty string as a credential for a credential-less mode —
    // the backend distinguishes "none" from "blank".
    credential: effectiveCredential,
  });

  const onTest = async () => {
    setBusy("test");
    setTestResult(null);
    try {
      const token = await getToken();
      const result = await testAdhocConnection(token, testPayload());
      setTestResult(result);
    } catch (err) {
      toastApiError("Test failed", err);
    } finally {
      setBusy(null);
    }
  };

  const onDiscover = async () => {
    setBusy("discover");
    setDiscovered(null);
    try {
      const token = await getToken();
      const result = await discoverDeployments(token, {
        endpoint: aiEndpoint.trim(),
        api_key: credential || null,
      });
      setDiscovered(result.items);
      // Pre-select everything found: the common case is "offer all of them",
      // and de-selecting is easier than hunting for the one you want.
      setChosenDeployments(result.items.map((d) => d.name));
      if (result.items.length === 0) {
        toastApiError(
          "No model deployments found",
          new Error(
            "This endpoint reported no model deployments. Check the endpoint " +
              "URL and that a model has been deployed on the resource.",
          ),
        );
      }
    } catch (err) {
      toastApiError("Could not list model deployments", err);
    } finally {
      setBusy(null);
    }
  };

  const onSave = async () => {
    setBusy("save");
    try {
      const token = await getToken();
      const input: CreateConnectionIn = {
        source_type: sourceType,
        display_name: displayName.trim(),
        classification: classification as CreateConnectionIn["classification"],
        auth_mode: authMode,
        config,
        credential: effectiveCredential,
      };
      const created = await createConnection(token, input);
      toastSuccess("Connection created", `${created.display_name} is ready.`);
      navigate(`/integration/connections/${created.connection_id}`);
    } catch (err) {
      toastApiError("Could not create connection", err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Stack gap="md" maw={640}>
      <Title order={2}>New connection</Title>

      <Paper withBorder radius="md" p="lg">
        <Stack gap="md">
          <Select
            label={<LabelWithHelp label="Source type" help={CONNECTION_HELP.sourceType} />}
            value={sourceType}
            onChange={onSourceChange}
            data={[
              { value: "veeva", label: "Veeva Vault" },
              { value: "blob", label: "Azure Blob Storage" },
              { value: "s3", label: "AWS S3" },
              { value: "ai_foundry", label: "Azure AI Foundry" },
              { value: "azure_devops", label: "Azure DevOps" },
              { value: "database", label: "Database" },
              { value: "cara", label: "Generis CARA" },
            ]}
            allowDeselect={false}
            disabled={sourceLocked}
          />

          <TextInput
            label="Display name"
            placeholder="Primary Vault"
            value={displayName}
            onChange={(e) => setDisplayName(e.currentTarget.value)}
            required
          />

          <Select
            label={
              <LabelWithHelp label="Data classification" help={CONNECTION_HELP.classification} />
            }
            description="Drives encryption, retention, and DSAR behaviour. Required."
            value={classification}
            onChange={(v) => setClassification(v ?? "confidential")}
            data={CLASSIFICATION_OPTIONS}
            allowDeselect={false}
            required
          />

          {AUTH_MODES[sourceType].length > 1 && (
            <Select
              label={
                <LabelWithHelp
                  label="Authentication mode"
                  help={
                    sourceType === "s3"
                      ? CONNECTION_HELP.authModeS3
                      : sourceType === "blob"
                        ? CONNECTION_HELP.authModeBlob
                        : CONNECTION_HELP.authModeVeeva
                  }
                />
              }
              value={authMode}
              onChange={(v) => {
                setAuthMode(v ?? AUTH_MODES[sourceType][0]?.value ?? "");
                setTestResult(null);
              }}
              data={AUTH_MODES[sourceType]}
              allowDeselect={false}
            />
          )}

          {sourceType === "veeva" && (
            <>
              <TextInput
                label={<LabelWithHelp label="Vault URL" help={CONNECTION_HELP.vaultUrl} />}
                placeholder="https://mytenant.veevavault.com"
                value={vaultUrl}
                onChange={(e) => setVaultUrl(e.currentTarget.value)}
                required
              />
              {authMode === "password" && (
                <TextInput
                  label={
                    <LabelWithHelp
                      label="Service-account username"
                      help={CONNECTION_HELP.username}
                    />
                  }
                  value={username}
                  onChange={(e) => setUsername(e.currentTarget.value)}
                />
              )}
              <TextInput
                label={<LabelWithHelp label="API version" help={CONNECTION_HELP.apiVersion} />}
                value={apiVersion}
                onChange={(e) => setApiVersion(e.currentTarget.value)}
                w={160}
              />
            </>
          )}

          {sourceType === "s3" && (
            <>
              <TextInput
                label={<LabelWithHelp label="Region" help={CONNECTION_HELP.s3Region} />}
                placeholder="eu-west-1"
                description="Where the bucket lives. Recorded on every access as GDPR residency evidence."
                value={region}
                onChange={(e) => setRegion(e.currentTarget.value)}
                required
                w={220}
              />
              {authMode !== "access_key" && (
                <TextInput
                  label={<LabelWithHelp label="Role ARN" help={CONNECTION_HELP.s3RoleArn} />}
                  placeholder="arn:aws:iam::123456789012:role/daquma-reader"
                  value={roleArn}
                  onChange={(e) => setRoleArn(e.currentTarget.value)}
                  required
                />
              )}
              {authMode === "assume_role" && (
                <TextInput
                  label={<LabelWithHelp label="External ID" help={CONNECTION_HELP.s3ExternalId} />}
                  description="Required. Must match the ExternalId condition in the role's trust policy."
                  value={externalId}
                  onChange={(e) => setExternalId(e.currentTarget.value)}
                  required
                />
              )}
              {!isS3Credentialless && (
                <TextInput
                  label={
                    <LabelWithHelp label="Access key ID" help={CONNECTION_HELP.s3AccessKeyId} />
                  }
                  placeholder="AKIA…"
                  description={
                    authMode === "assume_role"
                      ? "The base identity used only to assume the role above — grant it nothing else."
                      : undefined
                  }
                  value={accessKeyId}
                  onChange={(e) => setAccessKeyId(e.currentTarget.value)}
                  required
                />
              )}
              {isS3Credentialless && (
                <Alert variant="light" color="blue" title="No credential to store">
                  DAQUMA authenticates with a token minted from its own managed identity and
                  exchanges it for short-lived AWS credentials. Nothing secret is stored for this
                  connection.
                </Alert>
              )}
            </>
          )}

          {sourceType === "ai_foundry" && (
            <>
              <TextInput
                label={<LabelWithHelp label="Endpoint URL" help={CONNECTION_HELP.aiEndpoint} />}
                description={
                  "Your AI resource → Keys and Endpoint. Any form Azure shows works " +
                  "— a trailing /openai or /openai/v1 is trimmed automatically."
                }
                placeholder="https://my-resource.openai.azure.com"
                value={aiEndpoint}
                onChange={(e) => {
                  setAiEndpoint(e.currentTarget.value);
                  // The old list belongs to the old endpoint — clearing it stops
                  // a stale model surviving an endpoint change.
                  setDiscovered(null);
                  setChosenDeployments([]);
                }}
                required
              />

              <PasswordInput
                label={<LabelWithHelp label="API key" help={CONNECTION_HELP.credential} />}
                description={
                  "Same page as the endpoint — KEY 1 or KEY 2. Stored in Azure Key " +
                  "Vault and never shown again after saving."
                }
                placeholder="Keys and Endpoint → KEY 1"
                value={credential}
                onChange={(e) => {
                  setCredential(e.currentTarget.value);
                  setDiscovered(null);
                  setChosenDeployments([]);
                }}
                required
              />

              <Stack gap="xs">
                <Group justify="space-between" align="flex-end">
                  <LabelWithHelp label="Model deployments" help={CONNECTION_HELP.aiDeployments} />
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconSearch size={14} />}
                    onClick={() => void onDiscover()}
                    loading={busy === "discover"}
                    disabled={busy !== null || !aiEndpoint.trim() || !credential.trim()}
                  >
                    Discover models
                  </Button>
                </Group>

                {discovered === null && (
                  <Text size="sm" c="dimmed">
                    Enter the endpoint and key above, then press Discover models — the list comes
                    from the resource itself, so you can only pick a model that is really deployed.
                  </Text>
                )}

                {discovered !== null && discovered.length === 0 && (
                  <Text size="sm" c="dimmed">
                    This endpoint reported no model deployments. Check the endpoint URL and that a
                    model has been deployed on the resource.
                  </Text>
                )}

                {discovered !== null && discovered.length > 0 && (
                  <Chip.Group multiple value={chosenDeployments} onChange={setChosenDeployments}>
                    <Group gap="xs">
                      {discovered.map((d) => (
                        <Chip key={d.name} value={d.name} variant="outline">
                          {d.label}
                        </Chip>
                      ))}
                    </Group>
                  </Chip.Group>
                )}
              </Stack>
            </>
          )}

          {sourceType === "cara" && (
            <>
              <TextInput
                label={<LabelWithHelp label="Base URL" help={CONNECTION_HELP.caraBaseUrl} />}
                description="Include CARA's application path — https://host/cara, not just the host."
                placeholder="https://your-tenant.cara.cloud/cara"
                value={caraBaseUrl}
                onChange={(e) => setCaraBaseUrl(e.currentTarget.value)}
                required
              />
              <Alert variant="light" color="violet" icon={<IconInfoCircle size={18} />}>
                <Text size="sm">
                  DAQUMA reads CARA and cannot write to it — the connector can only list object
                  types, read their fields, and search. Ask your CARA administrator for a{" "}
                  <strong>read-only service account</strong> anyway: it puts the same guarantee on
                  your side of the trust boundary.
                </Text>
              </Alert>
            </>
          )}

          {sourceType === "azure_devops" && (
            <>
              <TextInput
                label="Organisation URL"
                description="The collection URL, e.g. https://dev.azure.com/DaqumaDK"
                placeholder="https://dev.azure.com/your-org"
                value={adoOrgUrl}
                onChange={(e) => setAdoOrgUrl(e.currentTarget.value)}
                required
              />
              <TextInput
                label="Project"
                description="The Azure DevOps project defects are created in."
                placeholder="DAQUMA PLATFORM"
                value={adoProject}
                onChange={(e) => setAdoProject(e.currentTarget.value)}
                required
              />
            </>
          )}

          {sourceType === "database" && (
            <>
              <Group grow align="flex-start">
                <Select
                  label={<LabelWithHelp label="Engine" help={CONNECTION_HELP.databaseEngine} />}
                  data={engines.map((e) => ({ value: e.engine, label: e.label }))}
                  value={dbEngine}
                  onChange={(v) => {
                    setDbEngine(v ?? "postgresql");
                    setTestResult(null);
                  }}
                  allowDeselect={false}
                  placeholder={engines.length ? "Pick an engine" : "Loading…"}
                />
                <NumberInput
                  label="Port"
                  description={
                    engineSpec ? `Default ${engineSpec.default_port}` : "The engine's default"
                  }
                  placeholder={engineSpec ? String(engineSpec.default_port) : ""}
                  value={dbPort}
                  min={1}
                  max={65535}
                  onChange={(v) => setDbPort(typeof v === "number" ? v : "")}
                />
              </Group>

              <TextInput
                label={<LabelWithHelp label="Host" help={CONNECTION_HELP.databaseHost} />}
                placeholder="pv-legacy.database.windows.net"
                value={dbHost}
                onChange={(e) => setDbHost(e.currentTarget.value)}
                required
              />

              <TextInput
                label={engineSpec?.addresses === "service_name" ? "Service name" : "Database"}
                description={
                  engineSpec?.addresses === "service_name"
                    ? "Oracle addresses a service, e.g. FREEPDB1 — not a database name."
                    : "The database to read from on that server."
                }
                value={dbName}
                onChange={(e) => setDbName(e.currentTarget.value)}
                required
              />

              <TextInput
                label={<LabelWithHelp label="Username" help={CONNECTION_HELP.databaseAccount} />}
                description="Use an account with SELECT and nothing else."
                value={dbUsername}
                onChange={(e) => setDbUsername(e.currentTarget.value)}
                required
              />

              <Checkbox
                label={
                  <LabelWithHelp
                    label="Require an encrypted connection"
                    help={CONNECTION_HELP.databaseTls}
                  />
                }
                checked={dbTls}
                onChange={(e) => {
                  setDbTls(e.currentTarget.checked);
                  setTestResult(null);
                }}
              />

              <Alert variant="light" color="blue" icon={<IconInfoCircle size={18} />}>
                <Text size="sm">
                  This connection reads. The platform has no code path that writes to a source
                  database — anything that is not a single SELECT is refused before the driver sees
                  it, and every connection runs in a read-only transaction. Give it a read-only
                  account anyway: that puts the same guarantee on your side of the line.
                </Text>
              </Alert>
            </>
          )}

          {sourceType !== "ai_foundry" && !isS3Credentialless && (
            <PasswordInput
              label={<LabelWithHelp label={credentialLabel} help={CONNECTION_HELP.credential} />}
              description="Stored in Azure Key Vault — never shown again after saving."
              value={credential}
              onChange={(e) => setCredential(e.currentTarget.value)}
              required
            />
          )}

          {testResult && (
            <Alert
              variant="light"
              color={testResult.ok ? "green" : "red"}
              title={testResult.ok ? "Connection healthy" : "Connection failing"}
            >
              {testResult.ok
                ? `The connection responded OK (${JSON.stringify(testResult.detail)}).`
                : String(
                    testResult.detail?.message ??
                      `The probe could not reach the system (${String(
                        testResult.detail?.error ?? "unknown error",
                      )}).`,
                  )}
            </Alert>
          )}

          {sourceType === "ai_foundry" && (
            <Alert
              variant="light"
              color="yellow"
              icon={<IconInfoCircle size={18} />}
              title="About this key"
            >
              <Text size="sm">
                The key is stored in Azure Key Vault and never shown again. Note that a key is a
                secret you have to rotate — a deployed environment should instead use a managed
                identity, which stores nothing at all. That mode has no setup form because a managed
                identity only exists inside Azure.
              </Text>
            </Alert>
          )}

          <Group justify="flex-end" gap="xs">
            <Button
              variant="default"
              leftSection={<IconPlugConnected size={16} />}
              onClick={() => void onTest()}
              loading={busy === "test"}
              disabled={!valid || busy !== null}
            >
              Test
            </Button>
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={() => void onSave()}
              loading={busy === "save"}
              disabled={!valid || busy !== null}
            >
              Save connection
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}
